import React from 'react';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import { PIPELINE_FUNNEL_STAGES, STAGE_BY_ID, topClientsByFica } from '../../utils/dashboardData';

function fmtMoney(n) {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000) return '$' + (v / 1000).toFixed(v >= 1_000_000 ? 2 : 1).replace(/\.0$/, '') + 'k';
  return '$' + v.toLocaleString('en-US');
}

export default function PipelineFunnel({ counts, clients, selectedStage, onStageClick, onClientClick, onViewAllClick }) {
  const top = topClientsByFica(clients, 10);

  return (
    <div
      data-testid="dashboard-pipeline"
      style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
          Pipeline
        </h3>
        {selectedStage && selectedStage !== 'all' && (
          <button
            type="button"
            data-testid="pipeline-clear-filter"
            onClick={() => onStageClick(null)}
            style={{ fontSize: 11, fontWeight: 700, background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
          >
            Clear stage filter
          </button>
        )}
      </div>

      {/* Funnel row */}
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, marginBottom: 18 }}>
        {PIPELINE_FUNNEL_STAGES.map((stageId, idx) => {
          const meta = STAGE_BY_ID[stageId];
          const count = counts[stageId] || 0;
          const isSelected = selectedStage === stageId;
          return (
            <React.Fragment key={stageId}>
              <button
                type="button"
                data-testid={`pipeline-stage-${stageId}`}
                onClick={() => onStageClick(isSelected ? null : stageId)}
                style={{
                  flex: 1,
                  background: meta.bg,
                  border: isSelected ? `2px solid ${meta.fg}` : `1px solid ${meta.bg}`,
                  borderRadius: 10,
                  padding: '14px 8px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(2,6,23,0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ fontSize: 26, fontWeight: 800, color: meta.fg, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {count}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: meta.fg, marginTop: 6, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  {meta.label}
                </div>
              </button>
              {idx < PIPELINE_FUNNEL_STAGES.length - 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textLight, fontSize: 14 }}>→</div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Top clients table */}
      <div style={{ borderTop: `1px solid ${C.borderLight}`, paddingTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: 0 }}>
            Top clients{selectedStage && selectedStage !== 'all' ? ` — ${STAGE_BY_ID[selectedStage]?.label || ''}` : ''}
          </h4>
          <button
            type="button"
            data-testid="pipeline-view-all-clients"
            onClick={onViewAllClick}
            style={{ fontSize: 11, fontWeight: 700, background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer' }}
          >
            View all in Clients tab →
          </button>
        </div>
        {top.length === 0 ? (
          <div style={{ padding: '30px 0', textAlign: 'center', color: C.textLight, fontSize: 13 }}>
            <Icon name="folder-open-outline" style={{ fontSize: 28, display: 'block', margin: '0 auto 8px' }} />
            No clients match the current filters.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {top.map((c) => {
              const stageMeta = STAGE_BY_ID[c?.crm?.stage] || STAGE_BY_ID.lead;
              const fica = Number(c?.summary?.annualEmployerFica) || 0;
              const lives = Number(c?.summary?.totalEmployees) || 0;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`pipeline-client-${c.id}`}
                  onClick={() => onClientClick && onClientClick(c)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr 110px 130px',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 8px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${C.borderLight}`,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{
                    background: stageMeta.bg, color: stageMeta.fg,
                    fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    display: 'inline-block', textAlign: 'center',
                  }}>{stageMeta.label}</span>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c?.company?.legalName || c?.company?.dba || 'Unnamed'}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMid }}>
                    {lives ? `${lives} lives` : '— lives'}
                    {c?.company?.address?.state ? ` · ${c.company.address.state}` : ''}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: fica > 0 ? C.green : C.textLight, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>
                    {fica > 0 ? fmtMoney(fica) + '/yr' : '—'}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
